import { motion } from 'framer-motion';
import { useLanguage } from '@/i18n/LanguageContext';
import { lc } from '@/i18n/copy';
import { Shield, Lock, Settings } from 'lucide-react';

const copy = {
  en: {
    heading: 'Your words are private unless you choose to share them.',
    points: [
      { icon: 'lock', text: 'Messages are secure and private.' },
      { icon: 'shield', text: 'Anonymous messages are moderated and traceable internally for safety.' },
      { icon: 'settings', text: 'You control when and how messages are sent.' },
    ],
  },
  es: {
    heading: 'Tus palabras son privadas a menos que elijas compartirlas.',
    points: [
      { icon: 'lock', text: 'Los mensajes son seguros y privados.' },
      { icon: 'shield', text: 'Los mensajes anónimos son moderados y rastreables internamente por seguridad.' },
      { icon: 'settings', text: 'Tú controlas cuándo y cómo se envían los mensajes.' },
    ],
  },
  pt: { heading: 'Suas palavras são privadas a menos que você escolha compartilhá-las.', points: [{ icon: 'lock', text: 'As mensagens são seguras e privadas.' }, { icon: 'shield', text: 'Mensagens anônimas são moderadas e rastreáveis internamente por segurança.' }, { icon: 'settings', text: 'Você controla quando e como as mensagens são enviadas.' }] },
  fr: { heading: 'Vos mots sont privés sauf si vous choisissez de les partager.', points: [{ icon: 'lock', text: 'Les messages sont sécurisés et privés.' }, { icon: 'shield', text: 'Les messages anonymes sont modérés et traçables en interne pour la sécurité.' }, { icon: 'settings', text: 'Vous contrôlez quand et comment les messages sont envoyés.' }] },
  ar: { heading: 'كلماتك خاصة ما لم تختر مشاركتها.', points: [{ icon: 'lock', text: 'الرسائل آمنة وخاصة.' }, { icon: 'shield', text: 'الرسائل المجهولة مُراقبة وقابلة للتتبع داخلياً للأمان.' }, { icon: 'settings', text: 'أنت تتحكم في متى وكيف يتم إرسال الرسائل.' }] },
  ja: { heading: '共有を選択しない限り、言葉はプライベートです。', points: [{ icon: 'lock', text: 'メッセージは安全でプライベートです。' }, { icon: 'shield', text: '匿名メッセージは安全のため内部で管理・追跡されます。' }, { icon: 'settings', text: 'メッセージの送信タイミングと方法はあなたが管理します。' }] },
  ko: { heading: '공유를 선택하지 않는 한 말은 비공개입니다.', points: [{ icon: 'lock', text: '메시지는 안전하고 비공개입니다.' }, { icon: 'shield', text: '익명 메시지는 안전을 위해 내부적으로 관리 및 추적됩니다.' }, { icon: 'settings', text: '메시지 전송 시기와 방법을 직접 관리합니다.' }] },
  id: { heading: 'Kata-kata Anda bersifat pribadi kecuali Anda memilih untuk membagikannya.', points: [{ icon: 'lock', text: 'Pesan aman dan pribadi.' }, { icon: 'shield', text: 'Pesan anonim dimoderasi dan dapat dilacak secara internal untuk keamanan.' }, { icon: 'settings', text: 'Anda mengontrol kapan dan bagaimana pesan dikirim.' }] },

  hi: {
    heading: 'आपके शब्द निजी हैं जब तक आप उन्हें साझा करना नहीं चुनते।',
    points: [
      { icon: 'lock', text: 'Messages are secure and private.' },
      { icon: 'shield', text: 'Anonymous messages are moderated and traceable internally for safety.' },
      { icon: 'सेटिंग्स', text: 'आप नियंत्रित करते हैं कि संदेश कब और कैसे भेजे जाते हैं।' },
    ],
  },
};

const iconMap = { lock: Lock, shield: Shield, settings: Settings };

export function TrustSafetySection() {
  const { language } = useLanguage();
  const t = lc(copy, language);

  return (
    <section className="py-16 md:py-24">
      <div className="container max-w-3xl mx-auto text-center">
        <motion.h2
          className="text-2xl md:text-3xl font-heading font-bold billboard-flash mb-10 !leading-tight"
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          {t.heading}
        </motion.h2>

        <div className="flex flex-col md:flex-row items-center justify-center gap-6 md:gap-10">
          {t.points.map((point, i) => {
            const Icon = iconMap[point.icon as keyof typeof iconMap];
            return (
              <motion.div
                key={i}
                className="flex items-center gap-3"
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
              >
                <Icon className="h-5 w-5 text-primary shrink-0" />
                <p className="text-sm text-muted-foreground text-left">{point.text}</p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
